/**
 * Shared shape of the in-app state. The mini app currently runs on local mock
 * data so the UI can be reviewed end-to-end without backend wiring; the API
 * adapter in `lib/api.ts` is still in place for the parts that already exist
 * on the server.
 */

export type ViewId =
    | 'menu'
    | 'games'
    | 'boxes'
    | 'boxGame'
    | 'fastGiveaway'
    | 'bogBattle'
    | 'battleLobby'
    | 'game667'
    | 'piggyBank'
    | 'profile'
    | 'bonuses'
    | 'bigGiveaway'
    | 'admin';

export type ModalId =
    | 'notifications'
    | 'withdraw'
    | 'createBattle'
    | 'createTask'
    | 'taskExec'
    | 'vipWarning'
    | 'giveawayRules'
    | 'deleteTask'
    | 'fastLose'
    | 'boxWin'
    | 'topUp'
    | 'profileEmoji'
    | 'taskPending'    // FIX FE-7: screenshot task submitted for review
    | 'appealReview';  // creator's re-review queue (пересмотры по моим заданиям)

export type CaseColor = 'green' | 'blue' | 'red';

export type CaseDef = {
    id: 'tiny' | 'normal' | 'huge';
    title: string;
    price: number;
    color: CaseColor;
    /**
     * Pool of 30 prizes (per spec). Exact distribution is described in
     * BSP BOXES.md: 15 blue, 8 yellow, 4 green, 2 orange, 1 red.
     */
    pool: Array<{ value: number; tier: 'blue' | 'yellow' | 'green' | 'orange' | 'red' }>;
};

export type Notification = {
    id: number;
    text: string;
    accent: 'gold' | 'green' | 'blue' | 'red';
    actionLabel?: string;
};

export type Task = {
    id: number;
    title: string;
    type: 'sub' | 'view' | 'ref' | 'review' | 'reaction';
    reward: number;
    holdHours: 0 | 12 | 48;
};

/** A task as GET /api/tasks returns it (see ServerTaskDto in lib/api). */
export type ServerTask = import('./lib/api').ServerTaskDto;

export type WithdrawRecord = {
    id: number;
    amount: number;
    method: 'stars' | 'gift' | 'ton' | 'cryptobot';
    target: string;
    status: 'pending' | 'sent' | 'failed';
    createdAt: string;
};

/* ---- Mock seed data ---------------------------------------------------- */

export const CASES: CaseDef[] = [
    {
        id: 'tiny',
        title: 'Mr. TINY',
        price: 5,
        color: 'green',
        pool: buildPool({ blue: 1, yellow: 3, green: 5, orange: 10, red: 20 }),
    },
    {
        id: 'normal',
        title: 'Mr. NORMAL',
        price: 50,
        color: 'blue',
        pool: buildPool({ blue: 10, yellow: 30, green: 50, orange: 100, red: 200 }),
    },
    {
        id: 'huge',
        title: 'Mr. HUGE',
        price: 200,
        color: 'red',
        pool: buildPool({ blue: 40, yellow: 120, green: 200, orange: 400, red: 800 }),
    },
];

function buildPool(values: Record<'blue' | 'yellow' | 'green' | 'orange' | 'red', number>) {
    const counts = { blue: 15, yellow: 8, green: 4, orange: 2, red: 1 } as const;
    const pool: CaseDef['pool'] = [];
    (Object.keys(counts) as Array<keyof typeof counts>).forEach((tier) => {
        for (let i = 0; i < counts[tier]; i++) pool.push({ value: values[tier], tier });
    });
    return pool;
}

export const INITIAL_TASKS: Task[] = [
    { id: 1, title: 'Подписаться на канал', type: 'sub', reward: 4, holdHours: 12 },
    { id: 2, title: 'Подписаться на канал', type: 'sub', reward: 2.5, holdHours: 48 },
    { id: 3, title: 'Подписаться на канал', type: 'sub', reward: 2, holdHours: 0 },
    { id: 4, title: 'Посмотреть пост', type: 'view', reward: 0.1, holdHours: 0 },
    { id: 5, title: 'Сделать рефку', type: 'ref', reward: 5, holdHours: 0 },
];

export const INITIAL_NOTIFS: Notification[] = [
    { id: 1, text: 'Доступно новое задание: +10 BSP', accent: 'gold', actionLabel: 'Перейти' },
    { id: 2, text: 'Приглашай друзей и получай BSP', accent: 'green', actionLabel: 'Рефералы' },
    { id: 3, text: 'Забирай ежедневный БОНУС на BSP', accent: 'blue', actionLabel: 'Забрать' },
];

export const PROFILE_EMOJIS = [
    '😎', '🔥', '👑', '🎰', '🦊', '🐉', '🦁', '🐺', '🤡', '👻',
    '💀', '🧠', '🤖', '👽', '🦅', '🐲', '🎯', '⚡', '💎', '🌟',
];

/**
 * Pick a random prize value from a case pool. Pure helper kept here so the
 * UI and any future backend logic can share the source of truth.
 */
export function pickFromPool(c: CaseDef) {
    return c.pool[Math.floor(Math.random() * c.pool.length)];
}
